<template>
  <NuxtImg
    v-if="imageObject.show"
    :src="imageObject.url"
    :alt="imageObject.alt"
    :title="imageObject.title"
    :height="imageHeight"
    :width="imageWidth"
    :sizes="props.sizes"
    :format="useFormat"
    :fit="fit"
    :fetchpriority="imageFetchpriority"
    :loading="imageLoading"
    :preload="imagePreload"
    :quality="quality"
    class="ui-img"
    :provider="isSvg ? 'none' : 'imgProxy'"
    :custom="isSvg"
  >
    <img
      :src="imageObject.url"
      :alt="imageObject.alt"
      :title="imageObject.title"
      :height="imageHeight"
      :width="imageWidth"
      :loading="imageLoading"
      :fetchpriority="imageFetchpriority"
      class="ui-img"
      :class="$attrs.class"
    />
  </NuxtImg>
</template>

<script setup lang="ts">
import type { T3File } from '@t3headless/nuxt-typo3'
import { computed } from 'vue'

import type { UiImgProps } from '~ui/types'

const props = withDefaults(defineProps<UiImgProps>(), { height: 0, width: 0 })

const imageObject = computed(() => {
  const getImageExtension = (url: string | null | undefined) =>
    url ? url.match(/(?:\.([^.]+))?$/)?.[1] : ''

  if (typeof props.image === 'object' && props.image !== null) {
    const image: T3File | undefined = Array.isArray(props.image)
      ? props.image?.[0]
      : props.image

    return {
      show: Object.keys(image ?? {}).length > 0,
      url: image?.publicUrl,
      alt: props.decorative ? '' : image?.properties?.alternative ?? '',
      title: props.decorative ? undefined : image?.properties?.title ?? undefined,
      extension: image?.properties?.extension ?? getImageExtension(image?.publicUrl),
      height: image?.properties?.dimensions?.height,
      width: image?.properties?.dimensions?.width
    }
  }

  return {
    show: !!props.image,
    url: props.image || '',
    alt: props.decorative ? '' : '',
    extension: getImageExtension(props.image)
  }
})

const isWrapperOnTop = computed(() => {
  return props.wrapperIndex === 0 || props.wrapperIndex === 1
})

const imageLoading = computed(() => {
  return props.loading || (isWrapperOnTop.value ? 'eager' : 'lazy')
})

const imageFetchpriority = computed(() => {
  if (props.fetchpriority) {
    return props.fetchpriority
  }
  if (imageLoading.value === 'lazy') {
    return 'low'
  }
  if (imageLoading.value === 'eager') {
    return 'high'
  }
  if (isWrapperOnTop.value) {
    return 'high'
  }
  return 'auto'
})

const imagePreload = computed((): boolean | { fetchPriority: 'auto' | 'high' | 'low' } | undefined => {
  if (props.preload || isWrapperOnTop.value || imageFetchpriority.value === 'high') {
    return { fetchPriority: 'high' }
  }
  return props.preload
})

const imageHeight = computed(() =>
  props.useImageSize
    ? imageObject.value?.height ?? 'auto'
    : props.height || 'auto'
)

const imageWidth = computed(() =>
  props.useImageSize
    ? imageObject.value?.width ?? 'auto'
    : props.width || 'auto'
)

const useFormat = computed(() => {
  return (
    props.format || (imageObject.value?.extension === 'svg' ? 'svg' : undefined)
  )
})

const isSvg = computed(() => {
  return imageObject.value?.extension === 'svg'
})
</script>
